import {
  EmptyButton,
  FieldLabel,
  Icon,
  InputField,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RoundButton,
  roundButtonStylesLarge,
} from "@smartsuite/react-ui/lib";
import * as S from "./style";
import { useRootStore } from "../../../stores/hooks";
import { useState } from "react";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import * as R from "remeda";
import type { AutomationConfig } from "@smartsuite/automation-engine-api";
import { LocalCssVariablesWrapper } from "../../App/style";
import { SolutionColorVariables, TextColorNames } from "@smartsuite/types";

export const DuplicateModal = observer<{
  label: string;
  onCancel: () => void;
  onDuplicate: (newLabel: string) => void;
}>(({ label, onDuplicate, onCancel }) => {
  const store = useRootStore();
  const automationsList = store.automations.list;

  const getDefaultNewLabel = (label: string): string => {
    let index = 0;
    let newLabel: string;
    const isItCopy = /\w*(copy|(copy \d+))+$/.test(label);
    const matchedIndex = label.match(/(\d+)$/);
    const copyIndex = matchedIndex !== null ? parseInt(matchedIndex[0]) : 0;
    const clearedLabel =
      isItCopy && copyIndex > 0
        ? label.substring(0, label.length - (copyIndex.toString().length + 1))
        : label;
    let withIndexItem: AutomationConfig | undefined;
    do {
      newLabel = clearedLabel;
      newLabel += isItCopy ? "" : ` ${t("copy")}`;
      newLabel += index > 0 ? ` ${index}` : "";
      withIndexItem = automationsList.find(
        (automation) => automation.label === newLabel,
      );
      index++;
    } while (withIndexItem !== undefined);
    return newLabel;
  };

  const [newLabel, setNewLabel] = useState(() => getDefaultNewLabel(label));
  const [inputFocus, setInputFocus] = useState(false);

  return (
    <ModalOverlay animation="fade" active={true} closeByEsc={false}>
      <LocalCssVariablesWrapper
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Modal
          size={"S"}
          onClose={onCancel}
          header={
            <ModalHeader
              title={t("3dot-menu.duplicate-automation")}
              iconSize="M"
              iconName={"layers"}
            />
          }
          footer={
            <ModalFooter
              buttons={
                <>
                  <div style={{ marginRight: "10px" }}>
                    <EmptyButton size="lg" onClick={onCancel}>
                      {t("general.cancel")}
                    </EmptyButton>
                  </div>
                  <RoundButton
                    styles={roundButtonStylesLarge}
                    onClick={() => {
                      onDuplicate(newLabel);
                    }}
                    disabled={!R.isTruthy(newLabel)}
                  >
                    {t("3dot-menu.duplicate-automation")}
                  </RoundButton>
                </>
              }
            ></ModalFooter>
          }
        >
          <S.ModalContent>
            <S.PreviewWrap>
              <S.PreviewLeft>
                <S.IconsWrap>
                  <Icon
                    size={"XL"}
                    name={"automations"}
                    color={TextColorNames.white}
                    dimension={{ width: 40, height: 40 }}
                  />
                </S.IconsWrap>
                <S.PreviewLabels>{label}</S.PreviewLabels>
              </S.PreviewLeft>
              <S.PreviewArrow>
                <Icon
                  size={"M"}
                  name={"arrow-right"}
                  color={SolutionColorVariables.sc1}
                />
              </S.PreviewArrow>
              <S.PreviewRight>
                <S.IconsWrap className={!R.isTruthy(newLabel) ? "error" : ""}>
                  <Icon
                    size={"XL"}
                    name={"automations"}
                    color={TextColorNames.white}
                    dimension={{ width: 40, height: 40 }}
                  />
                </S.IconsWrap>
                <S.PreviewLabels>{newLabel}</S.PreviewLabels>
              </S.PreviewRight>
            </S.PreviewWrap>
            <S.InputWrap>
              <FieldLabel color="primary">{t("name")}</FieldLabel>
              <InputField
                value={newLabel}
                onChange={({ target: { value } }) => setNewLabel(value)}
                maxLength={50}
                inputStyles={{
                  backgroundColor: "white",
                  borderColor: "G-1-5",
                  borderFocusColor: "solution-color",
                  borderHoverColor: "G-3",
                }}
                placeholder={
                  inputFocus ? t("automation-form.text-editor-type-input") : ""
                }
                type="text"
                state={inputFocus ? "active" : undefined}
                className={!R.isTruthy(newLabel) ? "error" : ""}
                onFocus={() => {
                  setInputFocus(true);
                }}
                onBlur={() => {
                  setInputFocus(false);
                }}
              />
            </S.InputWrap>
          </S.ModalContent>
        </Modal>
      </LocalCssVariablesWrapper>
    </ModalOverlay>
  );
});
